import React, { Component } from 'react';
import { browserHistory } from 'react-router';
import Helmet from 'react-helmet';
import {
  getShortTermToken,
  getThirdPartyRedirect,
  getUserTrackerConsentStatus,
} from '../../utils/ThirdPartyApiUtils';
import envConfig from '../../../static/config';
import LoaderComponent from '../common/LoaderComponent';
import { createMTrackConsent } from '../../actions/MTrackActionCreator';
import MTrackStore from '../../stores/MTrackStore';
import UserManagementStore from '../../stores/UserManagementStore';
import InternetBankingActions from '../../actions/InternetBankingActions';
import {
  CustomDashboard,
  Footer,
  Hero,
  KeyAreasCarousel,
  LogoCarousel,
} from './m-track';
import {
  xero,
  sage,
  facebook,
  mailchimp,
  quickbooks,
  shopify,
  wave,
  freshbooks,
  myob,
  freeagent,
  zoho,
  expensify,
  vend,
  unleashed,
  googleAnalytics,
  linkedin,
  instagram,
  talenox,
  googleWorkspace,
} from './m-track/logos';

import {
  injectContentSquareScript,
  sendPageView,
} from '../../utils/ContentSquareUtils';
import SessionStore from '../../stores/SessionStore';

/**
 * Error view
 */
const Error = ({ ctaAction, isVisible }) => (
  <div className={`mt-error ${isVisible ? '' : 'mt-hidden'}`} role="alert">
    <h1 className="mt-error__header">Sorry, something isn't right</h1>
    <p className="mt-strapline">
      We’re having trouble connecting you just now.
    </p>
    <button onClick={ctaAction} className="mt-button mt-button--primary">
      Try again
    </button>
  </div>
);

class MTrackPage extends Component {
  constructor() {
    super();

    this.onMTrackStoreChange = this.onMTrackStoreChange.bind(this);

    this.state = {
      isLoading: false,
      showError: false,
      hasMTrackConsentBeenCreated: MTrackStore.isMTrackConsentCreated(),
      redirectUrlWithToken: '',
      consentCheckCount: 0,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  componentWillMount() {
    MTrackStore.addChangeListener(this.onMTrackStoreChange);
    if (envConfig.isContentSquareEnabled && UserManagementStore.isCA()) {
      injectContentSquareScript();
      sendPageView('/m-track');
    }
  }

  componentWillUnmount() {
    MTrackStore.removeChangeListener(this.onMTrackStoreChange);

    this.backListener = browserHistory.listen((location) => {
      if (location.action === 'POP') {
        InternetBankingActions.navigateToWebTask('WEB-HOMEPAGE');
      }
    });
  }

  onMTrackStoreChange() {
    const mTrackConsentCreatedFlag = MTrackStore.isMTrackConsentCreated();

    if (!this.state.hasMTrackConsentBeenCreated && mTrackConsentCreatedFlag) {
      this.setState({
        hasMTrackConsentBeenCreated: true,
        consentCheckCount: 0,
      });
      this.attemptRedirectAfterConsentCheck();
    } else if (MTrackStore.isMTrackConsentCreatedFailed()) {
      this.showError();
    }
  }

  showError = () => {
    this.setState({
      isLoading: false,
      showError: true,
    });
  };

  attemptRedirectAfterConsentCheck = () => {
    setTimeout(async () => {
      await getUserTrackerConsentStatus();
      let consented = MTrackStore.isMTrackConsented();
      if (consented) {
        this.attemptRedirect();
      } else {
        this.setState(
          (state) => {
            return { consentCheckCount: state.consentCheckCount + 1 };
          },
          () => {
            if (
              this.state.consentCheckCount >
              envConfig.createConsentRedirectTimeout
            ) {
              this.showError();
            } else {
              this.attemptRedirectAfterConsentCheck();
            }
          }
        );
      }
    }, 1000);
  };

  attemptRedirect = async () => {
    this.setState({
      isLoading: true,
    });

    if (
      MTrackStore.isMTrackConsented() &&
      this.state.hasMTrackConsentBeenCreated
    ) {
      try {
        SessionStore.setTokenDelete(false);

        const redirectUrl = await getThirdPartyRedirect({});
        const token = await getShortTermToken();
        const redirectUrlWithToken = redirectUrl + '?id_token=' + token;
        /* history.pushState temp fix in place to allow back button navigation from 9S */
        const urlPath = window.location.href?.includes('?')
          ? window.location.href + '&customer_type=business'
          : window.location.href + '/?customer_type=business';
        window.history.pushState(null, '', urlPath);
        window.location.replace(redirectUrlWithToken);
      } catch (e) {
        this.showError();
      }
    } else {
      createMTrackConsent();
    }
  };

  render() {
    const { isLoading, showError } = this.state;

    return (
      <div className="m-track">
        <Helmet title="M-Track" />
        {isLoading ? (
          <LoaderComponent type="large" />
        ) : (
          <div role="main">
            <Error ctaAction={this.attemptRedirect} isVisible={showError} />
            <div className={isLoading || showError ? 'mt-hidden' : ''}>
              <Hero ctaAction={this.attemptRedirect} />
              <KeyAreasCarousel />
              <CustomDashboard ctaAction={this.attemptRedirect} />
              <LogoCarousel
                ctaAction={this.attemptRedirect}
                logos={[
                  xero,
                  sage,
                  facebook,
                  mailchimp,
                  quickbooks,
                  shopify,
                  wave,
                  freshbooks,
                  myob,
                  freeagent,
                  zoho,
                  expensify,
                  vend,
                  unleashed,
                  googleAnalytics,
                  linkedin,
                  instagram,
                  talenox,
                  googleWorkspace,
                ]}
              />
            </div>
          </div>
        )}
        <Footer
          className={showError ? 'mt-footer--error' : ''}
          links={[
            { href: 'https://uk.virginmoney.com/legals/', label: 'Legals' },
            {
              href: 'https://uk.virginmoney.com/security/',
              label: 'Privacy policy',
            },
            {
              href: 'https://uk.virginmoney.com/virgin/cookies/',
              label: 'Cookies',
            },
            {
              href: 'https://uk.virginmoney.com/virgin/accessibility.jsp',
              label: 'Accessibility',
            },
            {
              href: 'https://uk.virginmoney.com/contact',
              label: 'Help',
              icon: 'question',
            },
          ]}
        />
      </div>
    );
  }
}

export { MTrackPage };
